<template>
  <div class="data-config">
    <div class="container py-4">
      <!-- Step Header -->
      <div class="step-header mb-4">
        <h2 class="text-center">Step 3: Upload Your Data</h2>
        <p class="text-center text-muted">
          Upload an Excel file containing your indicator data
        </p>
      </div>

      <div class="row">
        <!-- Left Column: Upload Section -->
        <div class="col-lg-5">
          <b-card class="upload-card mb-4">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="fas fa-cloud-upload-alt mr-2 text-primary"></i>
                <strong>Upload Excel File</strong>
              </div>
            </template>

            <!-- File Upload Area -->
            <div
              class="upload-area"
              :class="{ 'drag-over': isDragOver, 'has-file': excelFile }"
              @dragover.prevent="isDragOver = true"
              @dragleave.prevent="isDragOver = false"
              @drop.prevent="handleDrop"
            >
              <input
                type="file"
                ref="fileInput"
                accept=".xlsx,.xls,.csv"
                class="d-none"
                @change="handleFileSelect"
              />

              <div v-if="!excelFile" class="upload-placeholder" @click="$refs.fileInput.click()">
                <i class="fas fa-file-excel fa-3x text-muted mb-3"></i>
                <p class="mb-1">Drag & drop your Excel file here</p>
                <p class="text-muted small">or click to browse</p>
                <p class="text-muted small mt-2">
                  Supports: .xlsx, .xls, .csv up to {{ formatFileSize(maxFileSizeBytes) }}
                  and {{ maxRowCount.toLocaleString() }} rows
                </p>
              </div>

              <div v-else class="file-info">
                <i class="fas fa-file-excel fa-2x text-success mb-2"></i>
                <p class="mb-1 font-weight-bold">{{ excelFile.name }}</p>
                <p class="text-muted small mb-2">{{ formatFileSize(excelFile.size) }}</p>
                <b-button variant="outline-danger" size="sm" @click="removeFile">
                  <i class="fas fa-times mr-1"></i>
                  Remove
                </b-button>
              </div>
            </div>

            <!-- Loading State -->
            <div v-if="isProcessing" class="text-center mt-3">
              <b-spinner variant="primary" small></b-spinner>
              <span class="ml-2">Processing file...</span>
            </div>

            <!-- Error Message -->
            <b-alert v-if="error" show variant="danger" class="mt-3" dismissible @dismissed="error = null">
              <i class="fas fa-exclamation-circle mr-2"></i>
              {{ error }}
            </b-alert>

            <!-- Success Message -->
            <b-alert v-if="success" show variant="success" class="mt-3" dismissible @dismissed="success = null">
              <i class="fas fa-check-circle mr-2"></i>
              {{ success }}
            </b-alert>
          </b-card>

          <!-- Sample Data Card -->
          <b-card class="sample-card">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="fas fa-database mr-2 text-info"></i>
                <strong>No Data? Try Sample Data</strong>
              </div>
            </template>

            <p class="text-muted small mb-3">
              Load pre-configured demo data with sample health indicators for testing.
            </p>

            <b-button variant="outline-primary" block @click="loadSampleData">
              <i class="fas fa-magic mr-2"></i>
              Load Sample Data
            </b-button>
          </b-card>

          <!-- Data Format Guide -->
          <b-card class="format-guide-card mt-4">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="fas fa-info-circle mr-2 text-warning"></i>
                <strong>Expected Data Format</strong>
              </div>
            </template>

            <p class="text-muted small mb-2">Your Excel file should contain columns like:</p>
            <ul class="format-list small">
              <li><strong>Indicator</strong> - Name or ID of the indicator</li>
              <li><strong>Location</strong> - State, LGA, or region name</li>
              <li><strong>Period/Year</strong> - Time period for the data</li>
              <li><strong>Value</strong> - Numeric data value</li>
              <li><strong>Data Source</strong> - Source of the data</li>
            </ul>
          </b-card>
        </div>

        <!-- Right Column: Preview Section -->
        <div class="col-lg-7">
          <b-card class="preview-card">
            <template #header>
              <div class="d-flex align-items-center justify-content-between">
                <div>
                  <i class="fas fa-table mr-2 text-secondary"></i>
                  <strong>Data Preview</strong>
                </div>
                <b-form-select
                  v-if="sheetNames.length > 1"
                  v-model="selectedSheet"
                  :options="sheetOptions"
                  size="sm"
                  style="width: auto;"
                  @change="onSheetChange"
                ></b-form-select>
              </div>
            </template>

            <!-- No Data State -->
            <div v-if="!hasData" class="no-data-state text-center py-5">
              <i class="fas fa-table fa-4x text-muted mb-3"></i>
              <h5 class="text-muted">No Data Uploaded</h5>
              <p class="text-muted small">
                Upload an Excel file to see a preview of your data here
              </p>
            </div>

            <!-- Data Table -->
            <div v-else>
              <!-- Sheet Info -->
              <div class="sheet-info mb-3 d-flex justify-content-between align-items-center">
                <span class="text-muted small">
                  <i class="fas fa-file-alt mr-1"></i>
                  Sheet: <strong>{{ selectedSheet }}</strong>
                </span>
                <span class="text-muted small">
                  {{ currentSheetData.length }} rows × {{ tableFields.length }} columns
                </span>
              </div>

              <!-- Table -->
              <div class="table-responsive">
                <b-table
                  :items="previewRows"
                  :fields="tableFields"
                  striped
                  hover
                  small
                  bordered
                  class="data-table mb-0"
                >
                  <template #cell()="data">
                    <span class="cell-content" :title="String(data.value)">
                      {{ truncateValue(data.value) }}
                    </span>
                  </template>
                </b-table>
              </div>

              <!-- More rows indicator -->
              <div v-if="currentSheetData.length > 10" class="text-center text-muted small py-2 border-top">
                <i class="fas fa-ellipsis-h mr-1"></i>
                and {{ currentSheetData.length - 10 }} more rows...
              </div>

              <!-- Column Summary -->
              <div class="column-summary mt-3">
                <h6 class="text-muted small text-uppercase">
                  <i class="fas fa-columns mr-1"></i>
                  Detected Columns ({{ tableFields.length }})
                </h6>
                <div class="column-tags">
                  <b-badge
                    v-for="field in tableFields"
                    :key="field.key"
                    variant="light"
                    class="mr-1 mb-1"
                  >
                    {{ field.label }}
                  </b-badge>
                </div>
              </div>
            </div>
          </b-card>
        </div>
      </div>

      <!-- Navigation Buttons -->
      <div class="navigation-buttons mt-4 d-flex justify-content-between">
        <b-button variant="outline-secondary" @click="goBack">
          <i class="fas fa-arrow-left mr-2"></i>
          Back to Customize
        </b-button>
        <b-button
          variant="primary"
          :disabled="!hasData"
          @click="saveAndContinue"
        >
          Continue to Column Mapping
          <i class="fas fa-arrow-right ml-2"></i>
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex';
import { parseExcelFile, extractColumns } from '../../utils/dataTransformer';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const MAX_ROW_COUNT = 5000;

export default {
  name: 'DataConfig',

  data() {
    return {
      excelFile: null,
      isDragOver: false,
      isProcessing: false,
      error: null,
      success: null,
      selectedSheet: '',
      parsedSheets: {},
      sheetNames: [],
      maxFileSizeBytes: MAX_FILE_SIZE_BYTES,
      maxRowCount: MAX_ROW_COUNT,
    };
  },

  computed: {
    ...mapState('configurer', ['rawExcelData', 'selectedSheet as storedSheet']),

    hasData() {
      return Object.keys(this.parsedSheets).length > 0 && this.currentSheetData.length > 0;
    },

    sheetOptions() {
      return this.sheetNames.map((name) => ({
        value: name,
        text: name,
      }));
    },

    currentSheetData() {
      if (!this.selectedSheet || !this.parsedSheets[this.selectedSheet]) {
        return [];
      }
      return this.parsedSheets[this.selectedSheet];
    },

    previewRows() {
      return this.currentSheetData.slice(0, 10);
    },

    tableFields() {
      const columns = extractColumns(this.currentSheetData);
      return columns.map((col) => ({
        key: col,
        label: col,
        sortable: true,
      }));
    },
  },

  mounted() {
    // Load from store if exists
    if (this.rawExcelData && Object.keys(this.rawExcelData).length > 0) {
      this.parsedSheets = this.rawExcelData;
      this.sheetNames = Object.keys(this.rawExcelData);
      if (this.storedSheet) {
        this.selectedSheet = this.storedSheet;
      } else if (this.sheetNames.length > 0) {
        this.selectedSheet = this.sheetNames[0];
      }
    }
  },

  methods: {
    ...mapMutations('configurer', [
      'SET_RAW_EXCEL_DATA',
      'SET_SELECTED_SHEET',
      'SET_SHEET_NAMES',
      'SET_COLUMN_MAPPING',
      'SET_TRANSFORMED_DATA',
      'SET_DATA_SUMMARY',
    ]),
    ...mapActions('configurer', ['processExcelUpload']),

    handleDrop(event) {
      this.isDragOver = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },

    handleFileSelect(event) {
      const files = event.target.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },

    async processFile(file) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv',
      ];
      const validExtensions = ['.xlsx', '.xls', '.csv'];
      const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));

      if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
        this.error = 'Please upload a valid Excel file (.xlsx, .xls) or CSV file';
        return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        this.error = `File is too large. Please upload a file smaller than ${this.formatFileSize(MAX_FILE_SIZE_BYTES)}.`;
        return;
      }

      this.excelFile = file;
      this.isProcessing = true;
      this.error = null;
      this.success = null;

      try {
        const result = await parseExcelFile(file, { maxRows: MAX_ROW_COUNT });

        this.parsedSheets = result.sheets;
        this.sheetNames = result.sheetNames;

        if (this.sheetNames.length > 0) {
          this.selectedSheet = this.sheetNames[0];
        }

        // Save to store
        this.processExcelUpload({
          sheets: result.sheets,
          sheetNames: result.sheetNames,
        });

        this.success = `Successfully loaded ${file.name} with ${this.sheetNames.length} sheet(s) and ${result.totalRows.toLocaleString()} row(s)`;
      } catch (err) {
        console.error('Error processing file:', err);
        this.error = err.message || 'Failed to process the Excel file';
        this.excelFile = null;
      } finally {
        this.isProcessing = false;
      }
    },

    removeFile() {
      this.excelFile = null;
      this.parsedSheets = {};
      this.sheetNames = [];
      this.selectedSheet = '';
      this.success = null;

      // Clear store
      this.SET_RAW_EXCEL_DATA({});
      this.SET_SHEET_NAMES([]);
      this.SET_SELECTED_SHEET(null);
      this.SET_COLUMN_MAPPING(null);
      this.SET_TRANSFORMED_DATA([]);
      this.SET_DATA_SUMMARY(null);

      // Reset file input
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    onSheetChange() {
      this.SET_SELECTED_SHEET(this.selectedSheet);
    },

    loadSampleData() {
      // Create comprehensive sample health data
      const sampleData = [
        {
          indicator: 'Maternal Mortality Rate',
          location: 'Lagos',
          period: '2023',
          value: 512,
          datasource: 'NHMIS',
          value_type: 'per 100,000 live births',
        },
        {
          indicator: 'Maternal Mortality Rate',
          location: 'Kano',
          period: '2023',
          value: 876,
          datasource: 'NHMIS',
          value_type: 'per 100,000 live births',
        },
        {
          indicator: 'Maternal Mortality Rate',
          location: 'Oyo',
          period: '2023',
          value: 623,
          datasource: 'NHMIS',
          value_type: 'per 100,000 live births',
        },
        {
          indicator: 'Antenatal Care Coverage',
          location: 'Lagos',
          period: '2023',
          value: 89.2,
          datasource: 'NDHS',
          value_type: 'percentage',
        },
        {
          indicator: 'Antenatal Care Coverage',
          location: 'Kano',
          period: '2023',
          value: 67.8,
          datasource: 'NDHS',
          value_type: 'percentage',
        },
        {
          indicator: 'Antenatal Care Coverage',
          location: 'Oyo',
          period: '2023',
          value: 78.4,
          datasource: 'NDHS',
          value_type: 'percentage',
        },
        {
          indicator: 'Infant Mortality Rate',
          location: 'Lagos',
          period: '2023',
          value: 41,
          datasource: 'MICS',
          value_type: 'per 1,000 live births',
        },
        {
          indicator: 'Infant Mortality Rate',
          location: 'Kano',
          period: '2023',
          value: 73,
          datasource: 'MICS',
          value_type: 'per 1,000 live births',
        },
        {
          indicator: 'Infant Mortality Rate',
          location: 'Oyo',
          period: '2023',
          value: 52,
          datasource: 'MICS',
          value_type: 'per 1,000 live births',
        },
        {
          indicator: 'Under-5 Mortality Rate',
          location: 'Lagos',
          period: '2023',
          value: 55,
          datasource: 'MICS',
          value_type: 'per 1,000 live births',
        },
        {
          indicator: 'Under-5 Mortality Rate',
          location: 'Kano',
          period: '2023',
          value: 128,
          datasource: 'MICS',
          value_type: 'per 1,000 live births',
        },
        {
          indicator: 'Under-5 Mortality Rate',
          location: 'Oyo',
          period: '2023',
          value: 78,
          datasource: 'MICS',
          value_type: 'per 1,000 live births',
        },
        {
          indicator: 'BCG Coverage',
          location: 'Lagos',
          period: '2023',
          value: 94.5,
          datasource: 'NHMIS',
          value_type: 'percentage',
        },
        {
          indicator: 'BCG Coverage',
          location: 'Kano',
          period: '2023',
          value: 78.3,
          datasource: 'NHMIS',
          value_type: 'percentage',
        },
        {
          indicator: 'BCG Coverage',
          location: 'Oyo',
          period: '2023',
          value: 86.7,
          datasource: 'NHMIS',
          value_type: 'percentage',
        },
        {
          indicator: 'Skilled Birth Attendance',
          location: 'Lagos',
          period: '2022',
          value: 82.1,
          datasource: 'NDHS',
          value_type: 'percentage',
        },
        {
          indicator: 'Skilled Birth Attendance',
          location: 'Kano',
          period: '2022',
          value: 45.6,
          datasource: 'NDHS',
          value_type: 'percentage',
        },
        {
          indicator: 'Skilled Birth Attendance',
          location: 'Oyo',
          period: '2022',
          value: 71.3,
          datasource: 'NDHS',
          value_type: 'percentage',
        },
      ];

      this.parsedSheets = {
        'Health Indicators': sampleData,
      };
      this.sheetNames = ['Health Indicators'];
      this.selectedSheet = 'Health Indicators';
      this.excelFile = { name: 'sample_health_data.xlsx', size: 2048 };

      // Save to store
      this.processExcelUpload({
        sheets: this.parsedSheets,
        sheetNames: this.sheetNames,
      });

      this.success = 'Sample health indicator data loaded successfully!';

      this.$bvToast.toast('Demo data with 18 health indicators across 3 states has been loaded', {
        title: 'Sample Data Loaded',
        variant: 'success',
        solid: true,
      });
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`;
    },

    truncateValue(value) {
      const str = String(value);
      return str.length > 30 ? `${str.substring(0, 30)}...` : str;
    },

    goBack() {
      this.$router.push({ name: 'configure-customize' });
    },

    saveAndContinue() {
      if (!this.hasData) return;

      // Data is already saved to store, navigate to mapping
      this.$router.push({ name: 'configure-mapping' });
    },
  },
};
</script>

<style scoped>
.data-config {
  min-height: 100vh;
  /* background-color: #f8f9fa; Removed bg color to let layout gradient show */
}

/* Glass Cards */
.upload-card,
.sample-card,
.format-guide-card,
.preview-card {
  border: none;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.1);
  margin-bottom: 24px;
}

::v-deep .card-header {
  background-color: rgba(255, 255, 255, 0.5);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding: 1.25rem;
  border-radius: 20px 20px 0 0 !important;
}

.step-header h2 {
  color: #2c3e50;
  font-weight: 700;
}

.upload-area {
  border: 2px dashed rgba(0, 125, 83, 0.3);
  border-radius: 16px;
  padding: 3rem 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: rgba(255, 255, 255, 0.4);
}

.upload-area:hover {
  border-color: #007D53;
  background-color: rgba(0, 125, 83, 0.05);
  transform: translateY(-2px);
}

.upload-area.drag-over {
  border-color: #007D53;
  background-color: rgba(0, 125, 83, 0.1);
  transform: scale(1.02);
}

.upload-area.has-file {
  border-style: solid;
  border-color: #007D53;
  background-color: rgba(255, 255, 255, 0.8);
  cursor: default;
}

.format-list li {
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

/* Table Styling */
.table-responsive {
  max-height: 400px;
  overflow-y: auto;
  border-radius: 12px;
}

::v-deep .table {
    background: transparent;
}

::v-deep .table thead th {
    background-color: rgba(0, 125, 83, 0.1);
    color: #007D53;
    border: none;
    position: sticky;
    top: 0;
}

::v-deep .table-striped tbody tr:nth-of-type(odd) {
    background-color: rgba(0, 0, 0, 0.02);
}

::v-deep .table td {
    border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.column-tags .badge {
  font-weight: 500;
  font-size: 0.75rem;
  padding: 0.5rem 0.8rem;
  background: rgba(0, 125, 83, 0.1);
  color: #007D53;
  border: none;
  border-radius: 6px;
}

.navigation-buttons {
  padding: 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  background-color: rgba(255, 255, 255, 0.5);
  margin-top: 2rem;
  border-radius: 0 0 20px 20px;
  backdrop-filter: blur(10px);
}

::v-deep .btn-primary {
  background-color: #007D53;
  border-color: #007D53;
  border-radius: 10px;
  padding: 0.5rem 1.5rem;
}

::v-deep .btn-outline-secondary {
    border-radius: 10px;
    border-color: #b2bec3;
    color: #636e72;
}

::v-deep .btn-outline-secondary:hover {
    background-color: #b2bec3;
    color: white;
}
</style>
